import type { ModelDefinition } from "@/models.js";

export const moonshotModels = [
	{
		id: "kimi-k2.8-preview",
		name: "Kimi K2.8 Preview",
		description:
			"Moonshot's Kimi K2.8 preview model with vision, reasoning, tool calling, and a 1M-token context window.",
		family: "moonshot",
		releasedAt: new Date("2026-09-11"),
		output: ["text"],
		providers: [
			{
				providerId: "tencent",
				externalId: "kimi-k2.8-preview",
				inputPrice: "1e-6",
				cachedInputPrice: "0.25e-6",
				outputPrice: "4e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 1000000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				supportedToolChoicesWithThinkingDisabled: ["function"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "kimi-k2",
		name: "Kimi K2",
		description: "Moonshot's Kimi K2 model for general tasks.",
		family: "moonshot",
		releasedAt: new Date("2025-07-11"),
		providers: [
			{
				providerId: "groq",
				externalId: "moonshotai/kimi-k2-instruct",
				// Frequently over capacity on Groq (503)
				stability: "unstable",
				inputPrice: "1.0e-6",
				cachedInputPrice: "0.5e-6",
				outputPrice: "3.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 16384,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// 404 model_not_found: moonshotai/kimi-k2-instruct no longer served
				deactivatedAt: new Date("2026-06-05"),
			},
			{
				providerId: "novita",
				externalId: "moonshotai/kimi-k2-instruct",
				// Streaming tool calls produce malformed JSON arguments
				stability: "unstable",
				inputPrice: "0.57e-6",
				outputPrice: "2.3e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "moonshot",
				externalId: "kimi-k2-0905-preview",
				inputPrice: "0.6e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 128000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// 404 resource_not_found: kimi-k2-0905-preview no longer served
				deactivatedAt: new Date("2026-06-05"),
			},
			{
				providerId: "nebius",
				externalId: "moonshotai/Kimi-K2-Instruct",
				inputPrice: "0.5e-6",
				outputPrice: "2.4e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-04-25"),
			},
			{
				providerId: "bytedance",
				externalId: "kimi-k2-250905",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.12e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				// 404 InvalidEndpointOrModel.NotFound: kimi-k2-250905 no longer served
				deactivatedAt: new Date("2026-06-05"),
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "Moonshot-Kimi-K2-Instruct",
				inputPrice: "0.574e-6",
				outputPrice: "2.294e-6",
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				webSearch: true,
				jsonOutput: false,
				deactivatedAt: new Date("2026-07-08"),
			},
		],
	},
	{
		id: "kimi-k2-thinking",
		name: "Kimi K2 Thinking",
		description: "Kimi K2 with extended thinking capabilities.",
		family: "moonshot",
		releasedAt: new Date("2025-11-06"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2-thinking",
				// Moonshot rejects forced tool_choice ("required"/named function)
				// when thinking is enabled, which cannot be disabled on reasoning
				// models. Only "auto"/"none" are accepted.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "0.6e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-06-28"),
			},
			{
				providerId: "bytedance",
				externalId: "kimi-k2-thinking-251104",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.12e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				deactivatedAt: new Date("2026-06-28"),
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "kimi-k2-thinking",
				inputPrice: "0.574e-6",
				outputPrice: "2.294e-6",
				regions: [{ id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 16384,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "vertex-openai",
				externalId: "moonshotai/kimi-k2-thinking-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				regions: [{ id: "global" }],
			},
		],
	},
	{
		id: "kimi-k2-thinking-turbo",
		name: "Kimi K2 Thinking Turbo",
		description: "Faster turbo variant of Kimi K2 with extended thinking.",
		family: "moonshot",
		releasedAt: new Date("2025-11-06"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2-thinking-turbo",
				// Reasoning model: forced tool_choice is rejected while thinking is
				// on, so only "auto"/"none" are accepted.
				supportedToolChoices: ["auto", "none"],
				// Frequently overloaded (429 engine_overloaded_error)
				stability: "unstable",
				inputPrice: "1.15e-6",
				outputPrice: "8.0e-6",
				cachedInputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-06-28"),
			},
		],
	},
	{
		id: "kimi-k2.5",
		name: "Kimi K2.5",
		description:
			"Moonshot's native multimodal model with visual coding and agentic capabilities.",
		family: "moonshot",
		releasedAt: new Date("2026-01-26"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.5",
				// Reasoning model: forced tool_choice is rejected while thinking is
				// on, so only "auto"/"none" are accepted.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "0.6e-6",
				outputPrice: "3.0e-6",
				cachedInputPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				reasoning: true,
				// Moonshot thinking is a binary toggle (`thinking.type`), not a
				// graduated effort: none/minimal disable it, low..max enable it.
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "together-ai",
				externalId: "moonshotai/Kimi-K2.5",
				// Together pulled this off serverless entirely (verified 2026-08-18):
				// every request 400s with "Unable to access non-serverless model ...
				// create and start a new dedicated endpoint", on both this id and the
				// -fp4 variant.
				stability: "unstable",
				deactivatedAt: new Date("2026-08-18"),
				inputPrice: "0.5e-6",
				outputPrice: "2.8e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: false,
				jsonOutput: false,
				deprecatedAt: new Date("2026-05-21"),
			},
			{
				providerId: "nebius",
				// Streaming tool calls and response_format: json_object are unreliable on Nebius
				stability: "unstable",
				externalId: "moonshotai/Kimi-K2.5",
				deactivatedAt: new Date("2026-06-22"),
				inputPrice: "0.5e-6",
				cachedInputPrice: "0.02e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "kimi-k2.5",
				inputPrice: "0.574e-6",
				outputPrice: "3.011e-6",
				regions: [
					{ id: "eu-frankfurt" },
					{ id: "us-virginia" },
					{ id: "cn-beijing" },
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 98304,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "deepinfra",
				externalId: "moonshotai/Kimi-K2.5",
				deactivatedAt: new Date("2026-09-07"),
				inputPrice: "0.45e-6",
				cachedInputPrice: "0.07e-6",
				outputPrice: "2.25e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 131072,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "kimi-k2.6",
		name: "Kimi K2.6",
		description:
			"Moonshot's latest native multimodal model with long-term code writing, thinking, and agentic capabilities.",
		family: "moonshot",
		releasedAt: new Date("2026-04-20"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.6",
				// Reasoning model: forced tool_choice is rejected while thinking is
				// on, so only "auto"/"none" are accepted.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.16e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				// Moonshot thinking is a binary toggle (`thinking.type`), not a
				// graduated effort: none/minimal disable it, low..max enable it.
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "canopywave",
				externalId: "moonshotai/kimi-k2.6",
				// canopywave's kimi-k2.6 deployment 503s on every request ("No
				// available prefill workers") while other models on the same key
				// work (verified 2026-07-14)
				stability: "unstable",
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.16e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "moonshotai/kimi-k2.6",
				inputPrice: "0.8e-6",
				cachedInputPrice: "0.16e-6",
				outputPrice: "3.4e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "moonshotai/Kimi-K2.6",
				test: "skip",
				inputPrice: "1.2e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "4.5e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				// Together's deployment accepts any reasoning_effort string without
				// validating it, and no tier measurably changes the reasoning length,
				// so `none` — honoured through the `thinking` switch rather than
				// reasoning_effort — is the only effort this mapping really applies.
				reasoningEfforts: ["none"],
				requiresDisableThinkingParam: true,
				vision: true,
				tools: false,
				jsonOutput: false,
				// Together AI removes this model from its serverless API on
				// 2026-08-19 and warns that capacity may already be reduced before
				// then, so stop selecting it now and fail requests after the cutoff.
				// Their recommended replacement, Kimi K3, is already mapped.
				deprecatedAt: new Date("2026-08-07"),
				deactivatedAt: new Date("2026-08-19"),
			},
			{
				providerId: "nebius",
				externalId: "moonshotai/Kimi-K2.6",
				deactivatedAt: new Date("2026-08-31"),
				// Streaming tool calls are unreliable on this deployment: required
				// choices can end without a tool call, while named choices can leak raw
				// control tokens into argument deltas (verified 2026-07-22).
				stability: "unstable",
				// Only auto and none produced consistently valid streaming responses.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "0.95e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: undefined,
				quantization: "int4",
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				// JSON mode has intermittently returned markdown-fenced JSON, so
				// normalize it defensively in both modes.
				healStreamingJsonOutput: true,
			},
			{
				providerId: "baidu",
				externalId: "kimi-k2.6",
				deactivatedAt: new Date("2026-10-28"),
				// Mirrors the Moonshot mapping: the model rejects forced
				// tool_choice while thinking is on.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.16e-6",
				outputPrice: "4e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				// Qianfan ignores reasoning_effort="none"; its thinking switch works.
				requiresDisableThinkingParam: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "tencent",
				externalId: "kimi-k2.6",
				inputPrice: "0.858e-6",
				cachedInputPrice: "0.145e-6",
				outputPrice: "3.566e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none"],
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "kimi-k2.7-code",
		name: "Kimi K2.7 Code",
		description:
			"Kimi's most intelligent coding model with a native multimodal architecture supporting text, image, and video input, thinking modes, and agent tasks.",
		family: "moonshot",
		releasedAt: new Date("2026-06-12"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.7-code",
				// Reasoning model: forced tool_choice is rejected while thinking is
				// on, so only "auto"/"none" are accepted.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.19e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				// Thinking is always on for kimi-k2.7-code; `thinking.type:
				// "disabled"` errors upstream, so `none`/`minimal` are not offered.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "moonshotai/kimi-k2.7-code",
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.19e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				// Thinking is always on for kimi-k2.7-code: novita accepts `none` and
				// `minimal` but keeps reasoning, so only low..max are offered.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				streaming: true,
				vision: true,
				tools: true,
				// novita 400s on forced tool_choice for this deployment
				supportedToolChoices: ["auto", "none"],
				jsonOutput: true,
				jsonOutputSchema: true,
				// novita's deployment 400s on the developer role
				supportsDeveloperRole: false,
			},
			{
				providerId: "nebius",
				externalId: "moonshotai/Kimi-K2.7-Code",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.95e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				// The model card advertises 256K context, and Nebius accepted an
				// 84K-token prompt live (verified 2026-07-22).
				contextSize: 262144,
				maxOutput: undefined,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				// JSON mode returns the object in reasoning_content with empty
				// content on this deployment (verified 2026-07-22).
				jsonOutput: false,
			},
			{
				providerId: "tencent",
				externalId: "kimi-k2.7-code",
				inputPrice: "0.95e-6",
				cachedInputPrice: "0.19e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				// Thinking cannot be turned off: "none" 400s, every other tier is
				// accepted.
				reasoningEfforts: ["minimal", "low", "medium", "high", "xhigh", "max"],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none"],
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "kimi-k2.7-code-highspeed",
		name: "Kimi K2.7 Code Highspeed",
		description:
			"High-speed inference variant of Kimi's most intelligent coding model with a native multimodal architecture supporting text, image, and video input, thinking modes, and agent tasks.",
		family: "moonshot",
		releasedAt: new Date("2026-06-12"),
		providers: [
			{
				providerId: "moonshot",
				externalId: "kimi-k2.7-code-highspeed",
				// Reasoning model: forced tool_choice is rejected while thinking is
				// on, so only "auto"/"none" are accepted.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "1.9e-6",
				cachedInputPrice: "0.38e-6",
				outputPrice: "8.0e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				reasoning: true,
				// Thinking is always on for kimi-k2.7-code-highspeed; `thinking.type:
				// "disabled"` errors upstream, so `none`/`minimal` are not offered.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: false,
				supportedParameters: [
					"max_tokens",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "tencent",
				externalId: "kimi-k2.7-code-highspeed",
				inputPrice: "1.9e-6",
				cachedInputPrice: "0.38e-6",
				outputPrice: "8e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				// Thinking cannot be turned off ("none" 400s), but unlike the
				// direct Moonshot deployment this one does accept "minimal".
				reasoningEfforts: ["minimal", "low", "medium", "high", "xhigh", "max"],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none"],
				// Enforced here, unlike the Moonshot mapping: a json_object
				// request for a plain sentence comes back as JSON, and a strict
				// json_schema extraction conforms.
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "kimi-k3",
		name: "Kimi K3",
		description:
			"Kimi's flagship model for long-horizon coding and end-to-end knowledge work with a 1M-token context window.",
		family: "moonshot",
		releasedAt: new Date("2026-07-16"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "kimi-k3",
				inputPrice: "3e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15e-6",
				requestPrice: "0",
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "2.827e-6",
						cachedInputPrice: "0.283e-6",
						outputPrice: "14.133e-6",
					},
					{
						id: "us-virginia",
						inputPrice: "2.827e-6",
						cachedInputPrice: "0.283e-6",
						outputPrice: "14.133e-6",
					},
					{
						id: "cn-beijing",
						inputPrice: "2.827e-6",
						cachedInputPrice: "0.283e-6",
						outputPrice: "14.133e-6",
					},
				],
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "moonshot",
				externalId: "kimi-k3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				reasoning: true,
				// K3 always thinks; the effort level is set via the native
				// top-level `reasoning_effort` field (no K2-era `thinking`
				// toggle), which accepts low, high, and max (default max).
				reasoningEfforts: ["low", "high", "max"],
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "runpod",
				externalId: "kimi-k3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				requiresBase64Images: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				supportedToolChoicesWithThinkingDisabled: ["function"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "moonshotai/kimi-k3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "moonshotai/Kimi-K3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 1000000,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				// Together's deployment accepts any reasoning_effort string without
				// validating it, and no tier measurably changes the reasoning length,
				// so `none` — honoured through the `thinking` switch rather than
				// reasoning_effort — is the only effort this mapping really applies.
				reasoningEfforts: ["none"],
				requiresDisableThinkingParam: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "nebius",
				externalId: "moonshotai/Kimi-K3",
				deactivatedAt: new Date("2026-08-31"),
				// Named tool choice is rejected with a 400 ("Named tool choice is not
				// supported for Kimi K3"), and "required" is silently broken: the
				// upstream emits the call as text in `reasoning_content` and returns
				// `finish_reason: "stop"` with no `tool_calls`.
				supportedToolChoices: ["auto", "none"],
				inputPrice: "3.0e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				// `response_format: {"type": "json_object"}` routes the whole answer
				// into `reasoning_content` and leaves `content` empty on this
				// deployment, so JSON mode is unusable here.
				jsonOutput: false,
				// The endpoint intermittently stalls: roughly one in eight simple
				// requests takes ~35s instead of the usual ~2s, and reasoning streams
				// occasionally run past 120s without completing. together-ai serves
				// the same model at the same price with steady ~2s latency, so keep
				// nebius out of automatic provider selection (it stays usable when
				// pinned explicitly) and out of the e2e suite, where the stalls blow
				// the per-test timeout budget.
				stability: "unstable",
				test: "skip",
			},
			{
				providerId: "fireworks",
				externalId: "accounts/fireworks/models/kimi-k3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				// Fireworks publishes a Priority rate card for Kimi K3 at exactly
				// 1.25x the standard rates ($3.75 / $0.375 / $18.75 per million),
				// which is the provider-level multiplier — no override needed.
				serviceTiers: ["priority"],
				contextSize: 1040384,
				maxOutput: 1040384,
				streaming: true,
				reasoning: true,
				// Unlike the native Moonshot deployment (which only takes "max"),
				// Fireworks accepts the full effort enum and rejects "minimal".
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "canopywave",
				externalId: "moonshotai/kimi-k3",
				// A named function choice is rejected with a 400 ("tool_choice
				// 'specified' is incompatible with thinking enabled"); "required"
				// works and reliably returns a tool call.
				supportedToolChoices: ["auto", "none", "required"],
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				// A 300K-token prompt was served correctly; prompts beyond ~1M are
				// rejected with a 429 because the upstream TPM quota is 1M.
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				// This deployment accepts every effort tier, including "none"
				// (which returns zero reasoning tokens) and "minimal".
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "tencent",
				externalId: "kimi-k3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				tools: true,
				// "required" is honoured, but a named function 400s on this
				// deployment.
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "aws-bedrock",
				externalId: "moonshotai.kimi-k3",
				apiFormat: "openai-chat-completions",
				crossRegionProfiles: true,
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				cacheWriteInputPrice: "3.75e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				serviceTiers: ["flex"],
				contextSize: 1048576,
				maxOutput: 1048576,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				vision: true,
				requiresBase64Images: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				// `top_p` is only accepted at 0.95, and non-zero frequency and
				// presence penalties are rejected.
				supportedParameters: [
					"temperature",
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
				regions: [{ id: "global" }, { id: "us" }],
			},
			{
				providerId: "baidu",
				externalId: "qianfan-vl-v1",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.3e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled: reasoning_effort "none" and
				// thinking.type "disabled" both still reason.
				reasoningEfforts: ["minimal", "low", "medium", "high", "xhigh", "max"],
				// reasoning_content is usually empty when the model goes straight
				// to a tool call, at every effort.
				reasoningOutput: "omit",
				vision: true,
				requiresBase64Images: true,
				tools: true,
				// A named function is rejected while thinking is on.
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutput: true,
				jsonOutputSchema: true,
				// Only temperature 1 and top_p 0.95 are accepted.
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "kimi-k3-fast",
		name: "Kimi K3 Fast",
		description:
			"Kimi K3 served on Fireworks' low-latency Fast router, trading a higher token price for faster generation.",
		family: "moonshot",
		releasedAt: new Date("2026-07-16"),
		providers: [
			{
				providerId: "fireworks",
				externalId: "accounts/fireworks/routers/kimi-k3-fast",
				inputPrice: "4.5e-6",
				cachedInputPrice: "0.45e-6",
				outputPrice: "22.5e-6",
				requestPrice: "0",
				contextSize: 1040384,
				maxOutput: 1040384,
				streaming: true,
				reasoning: true,
				// Like the standard Fireworks deployment, the full effort enum is
				// accepted and "minimal" is rejected with a 400.
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "kimi-k3.1-pro",
		name: "Kimi K3.1 Pro",
		description:
			"Kimi's flagship reasoning model with always-on thinking, vision input, tool calling, and a 1M-token context window.",
		family: "moonshot",
		releasedAt: new Date("2026-10-23"),
		providers: [
			{
				providerId: "runpod",
				externalId: "kimi-k3.1-pro",
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				requiresBase64Images: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				supportedToolChoicesWithThinkingDisabled: ["function"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "moonshot",
				externalId: "kimi-k3.1-pro",
				inputPrice: "2.0e-6",
				cachedInputPrice: "0.2e-6",
				cacheWriteInputPrice: "2.0e-6",
				cacheWriteInputPrice1h: "4.0e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				streaming: true,
				vision: true,
				requiresBase64Images: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "kimi-k3.1-pro-highspeed",
		name: "Kimi K3.1 Pro Highspeed",
		description:
			"High-speed inference variant of Kimi K3.1 Pro with always-on thinking, vision input, tool calling, and a 1M-token context window.",
		family: "moonshot",
		releasedAt: new Date("2026-10-23"),
		providers: [
			{
				providerId: "runpod",
				externalId: "kimi-k3.1-pro-highspeed",
				inputPrice: "4.0e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: true,
				requiresBase64Images: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				supportedToolChoicesWithThinkingDisabled: ["function"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
			{
				providerId: "moonshot",
				externalId: "kimi-k3.1-pro-highspeed",
				inputPrice: "4.0e-6",
				cachedInputPrice: "0.2e-6",
				cacheWriteInputPrice: "4.0e-6",
				cacheWriteInputPrice1h: "8.0e-6",
				outputPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: 1048576,
				reasoning: true,
				reasoningEfforts: ["low", "high", "max"],
				streaming: true,
				vision: true,
				requiresBase64Images: true,
				tools: true,
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"max_tokens",
					"response_format",
					"tools",
					"tool_choice",
					"reasoning_effort",
				],
			},
		],
	},
] as const satisfies ModelDefinition[];
